import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Container,
} from "@mui/material";
import Nav from "./Nav.jsx";

const ErrorPage = ({ error, errorInfo }) => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh",
      }}
    >
      <Nav />
      
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
            textAlign: "center",
            px: 2,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: "20px",
              boxShadow: "none",
              border: "1px solid #E0E0E0",
              maxWidth: "600px",
              width: "100%",
            }}
          >
            {/* Error Icon */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <img
                src="/Icons/error.gif"
                alt="Error"
                style={{ width: "120px", height: "120px" }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Box>

            {/* Error Title */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "24px", md: "32px" },
                color: "#253858",
                mb: 2,
              }}
            >
              Oops! Something went wrong
            </Typography>

            {/* Error Message */}
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", md: "18px" },
                color: "#5E6C84",
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              We're sorry, but something unexpected happened. Please try again or contact support if the problem persists.
            </Typography>

            {/* Error Details (for development) */}
            {process.env.NODE_ENV === "development" && error && (
              <Box
                sx={{
                  backgroundColor: "#F8F9FA",
                  p: 2,
                  borderRadius: "8px",
                  mb: 4,
                  textAlign: "left",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#DC3545",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {error.toString()}
                  {errorInfo && errorInfo.componentStack}
                </Typography>
              </Box>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={handleRefresh}
                sx={{
                  backgroundColor: "#3965FB",
                  color: "#FFFFFF",
                  px: 4,
                  py: 1.5,
                  borderRadius: "8px",
                  textTransform: "capitalize",
                  fontSize: "16px",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#335ecc",
                  },
                }}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                onClick={handleGoBack}
                sx={{
                  borderColor: "#3965FB",
                  color: "#3965FB",
                  px: 4,
                  py: 1.5,
                  borderRadius: "8px",
                  textTransform: "capitalize",
                  fontSize: "16px",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#335ecc",
                    backgroundColor: "rgba(57, 101, 251, 0.04)",
                  },
                }}
              >
                Go Back
              </Button>

              <Button
                variant="text"
                onClick={handleGoHome}
                sx={{
                  color: "#5E6C84",
                  px: 4,
                  py: 1.5,
                  textTransform: "capitalize",
                  fontSize: "16px",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "rgba(94, 108, 132, 0.04)",
                  },
                }}
              >
                Go Home
              </Button>
            </Box>

            {/* Support Contact */}
            <Typography
              variant="body2"
              sx={{
                color: "#5E6C84",
                mt: 4,
                fontSize: "14px",
              }}
            >
              Need help? Contact our support team at{" "}
              <a
                href="mailto:support@pbpay.com"
                style={{
                  color: "#3965FB",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                support@pbpay.com
              </a>
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default ErrorPage; 